import { Mic, Settings2, ShieldCheck, X } from "lucide-react";
import { useEffect, useState } from "react";

type PermissionStateValue = "prompt" | "granted" | "denied" | "unsupported";

const SEEN_KEY = "parlenote:microphone-explained";

async function microphonePermission(): Promise<PermissionStateValue> {
  if (!navigator.permissions?.query) return "unsupported";
  try {
    const status = await navigator.permissions.query({
      name: "microphone" as PermissionName
    });
    return status.state;
  } catch {
    return "unsupported";
  }
}

export function MicrophoneOnboarding({
  authenticated,
  onReady
}: {
  authenticated: boolean;
  onReady?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState<PermissionStateValue>("prompt");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authenticated) return;
    void microphonePermission().then((state) => {
      setPermission(state);
      if (state === "granted") {
        localStorage.setItem(SEEN_KEY, "1");
        return;
      }
      if (!localStorage.getItem(SEEN_KEY)) setOpen(true);
    });
  }, [authenticated]);

  if (!open) return null;

  const request = async () => {
    setBusy(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setPermission("granted");
      localStorage.setItem(SEEN_KEY, "1");
      setOpen(false);
      onReady?.();
    } catch {
      setPermission("denied");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        className="permission-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="microphone-title"
      >
        <button
          className="icon-button close-button"
          type="button"
          onClick={() => {
            localStorage.setItem(SEEN_KEY, "1");
            setOpen(false);
          }}
          aria-label="Plus tard"
        >
          <X aria-hidden="true" />
        </button>
        <div className="permission-icon" aria-hidden="true">
          <Mic />
        </div>
        <p className="eyebrow">Avant votre premier vocal</p>
        <h2 id="microphone-title">ParleNote a besoin du microphone</h2>
        <p>
          Après votre confirmation, votre téléphone affichera sa propre demande. Choisissez
          «&nbsp;Autoriser pendant l’utilisation du site&nbsp;» si cette option est proposée.
        </p>
        <div className="privacy-chip">
          <ShieldCheck aria-hidden="true" />
          L’enregistrement ne commence que lorsque vous touchez le bouton micro.
        </div>
        {permission === "denied" && (
          <div className="permission-help" role="alert">
            <Settings2 aria-hidden="true" />
            <span>
              L’accès est bloqué. Ouvrez les réglages du site dans votre navigateur,
              autorisez «&nbsp;Microphone&nbsp;», puis rechargez ParleNote.
            </span>
          </div>
        )}
        <button className="button primary wide" type="button" disabled={busy} onClick={request}>
          {busy ? "Ouverture…" : permission === "denied" ? "Réessayer" : "Autoriser le microphone"}
        </button>
        <button
          className="button text-only"
          type="button"
          onClick={() => {
            localStorage.setItem(SEEN_KEY, "1");
            setOpen(false);
          }}
        >
          Plus tard
        </button>
      </section>
    </div>
  );
}
